import React from 'react'
import { TouchableOpacity, View } from 'react-native'
import { useTheme } from '@ui-kitten/components'
import moment from 'moment'
import { useIsDarkMode } from '@src/config/theme'
import { SHORT_MONTH_AND_DATE_FORMAT } from '@config/momentFormat'
import { StyleService, useStyleSheet } from '@style/service'
import { ChatbotConversation } from '@src/types'
import { Icon, Text } from '@components/base'
import { getConversationIcon, renderHighlightedText, stripMarkdown } from '../utils'
import { ConversationTag } from './ConversationTag'

const formatConversationDate = (dateString: string) => {
  const date = moment(dateString)
  if (date.isSame(moment(), 'day')) {
    return 'Today'
  }
  if (date.isSame(moment().subtract(1, 'day'), 'day')) {
    return 'Yesterday'
  }
  return date.format(SHORT_MONTH_AND_DATE_FORMAT)
}

interface ConversationCardProps {
  conversation: ChatbotConversation
  onPress: (conversation: ChatbotConversation) => void
  searchQuery?: string
  hideChevron?: boolean
  showDate?: boolean
  previewLines?: number
}

export const ConversationCard = ({
  conversation,
  onPress,
  searchQuery,
  hideChevron,
  showDate,
  previewLines,
}: ConversationCardProps) => {
  const styles = useStyleSheet(themedStyle)
  const theme = useTheme()
  const isDarkMode = useIsDarkMode()
  const hasDate = !!showDate
  const hasTags = conversation.tags.length > 0
  const hasPreview = !!previewLines && !!conversation.preview
  const showNew =
    conversation.reportId &&
    (!conversation.read || moment().diff(moment(conversation.createdAt), 'minutes') < 5)
  const isTitleOnly = !hasDate && !hasTags && !hasPreview

  return (
    <TouchableOpacity
      style={[styles.card, isTitleOnly && styles.cardCentered, isDarkMode && styles.cardDark]}
      accessibilityLabel="See Conversation"
      onPress={() => onPress(conversation)}
    >
      <View>
        <View style={[styles.icon, styles.iconBackground]}>
          <Icon name={getConversationIcon(conversation)} size="16" />
        </View>
        {showNew && <View style={styles.unreadDot} />}
      </View>
      <View style={styles.flex1}>
        <View style={styles.titleRow}>
          <Text type="large" bold numberOfLines={1} style={styles.flex1}>
            {renderHighlightedText(conversation.title, searchQuery, 'large', styles.highlight)}
          </Text>
        </View>
        {hasDate && (
          <Text type="small" style={styles.dateLabel}>
            {formatConversationDate(conversation.createdAt)}
          </Text>
        )}
        {hasTags && (
          <View style={styles.tagsContainer}>
            {conversation.tags.map((tag) => (
              <ConversationTag key={tag} tag={tag} />
            ))}
          </View>
        )}
        {hasPreview && (
          <Text type="small" numberOfLines={previewLines} style={styles.previewText}>
            {renderHighlightedText(
              stripMarkdown(conversation.preview ?? ''),
              searchQuery,
              'small',
              styles.highlight,
            )}
          </Text>
        )}
      </View>
      {!hideChevron && (
        <Icon name="caret-right" size="20" weight="bold" color={theme['theme.text.tertiary']} />
      )}
    </TouchableOpacity>
  )
}

const themedStyle = StyleService.create({
  card: {
    backgroundColor: 'theme.background',
    borderRadius: 8,
    padding: 16,
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 12,
  },
  cardDark: {
    backgroundColor: 'theme.surface.base1',
  },
  cardCentered: {
    alignItems: 'center',
  },
  icon: {
    width: 32,
    height: 32,
    borderRadius: 25,
    alignItems: 'center',
    justifyContent: 'center',
  },
  iconBackground: {
    backgroundColor: 'theme.surface.base2',
  },
  flex1: {
    flex: 1,
  },
  titleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  unreadDot: {
    position: 'absolute',
    top: 0,
    right: 0,
    width: 10,
    height: 10,
    borderRadius: 5,
    backgroundColor: 'theme.error.base',
  },
  dateLabel: {
    color: 'theme.text.tertiary',
    marginTop: 2,
  },
  tagsContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 4,
    marginTop: 4,
  },
  highlight: {
    color: 'theme.info.base',
  },
  previewText: {
    color: 'theme.text.secondary',
    marginTop: 4,
  },
})
